// « Commenter », comme dans Google Docs (Marc, 06/10) — la bulle qui apparaît au coin d'une
// tuile ou d'un graphique, et les pastilles qui disent qu'un élément a déjà des fils.
//
// La bulle vit en bas à gauche de l'objet, sur la ligne du bouton (i) « Comprendre ce chiffre »
// (en bas à droite) : le coin droit du haut est déjà pris par l'épingle et le partage, et cette
// ligne est vide à gauche — rien n'est recouvert. Au repos elle est transparente mais reste un
// vrai bouton dans l'ordre de tabulation ; elle se montre au survol de l'objet et au focus
// clavier. Sans survol (téléphone, tablette), elle est toujours visible : c'est le chemin le plus
// simple vers l'écriture, sans appui long ni menu à découvrir.
//
// L'ancre précise (#563) : un graphique déclaré porte son identifiant ; une tuile, son LIBELLÉ
// (`tuile`), que le serveur garde s'il est déclaré par le code des builders (sinon le fil retombe
// sur la section). Chacun porte sa pastille ; celle de la section (`PastilleSection`) compte tous
// les fils de la section hors graphiques — une section couvre ses tuiles.
import { MessageSquare, MessageSquarePlus } from 'lucide-react'
import { useCanalTerObs } from './contexte'
import { ancreDe, useFilsTerObs } from './fils'

const commentaires = (n: number) => `${n} commentaire${n > 1 ? 's' : ''}`

export function CommenterElement({ titre, graphique, tuile, visible }: {
  titre: string; graphique?: string; tuile?: string; visible: boolean
}) {
  const canal = useCanalTerObs()
  const fils = useFilsTerObs()
  const ancre = fils ? ancreDe(canal, graphique, tuile) : null
  if (!fils || !ancre) return null
  const deCetElement = ancre.graphique ? fils.filsDuGraphique(ancre.graphique)
    : ancre.tuile ? fils.filsDeTuile(ancre.section, ancre.tuile) : []
  return (
    <span className="obs2-commenter-zone">
      {deCetElement.length > 0 && (
        <button type="button" className="obs2-fils-pastille"
          aria-label={`${commentaires(deCetElement.length)} sur « ${titre} »`}
          title={ancre.graphique ? 'Voir les commentaires sur ce graphique'
                                 : 'Voir les commentaires sur cette tuile'}
          onClick={e => {
            e.stopPropagation()
            fils.ouvrir(ancre.graphique ? { graphique: ancre.graphique }
              : { section: ancre.section ?? '', tuile: ancre.tuile! }, e.currentTarget)
          }}>
          <MessageSquare size={12} aria-hidden="true" />
          <span aria-hidden="true">{deCetElement.length}</span>
        </button>
      )}
      {canal?.ecriture !== false && (
      <button type="button" className={`obs2-commenter${visible ? ' visible' : ''}`}
        aria-label={`Commenter : ${titre}`}
        onClick={e => { e.stopPropagation(); fils.commenter({ ancre, titre }, e.currentTarget) }}>
        <MessageSquarePlus size={13} aria-hidden="true" />
        <span>Commenter</span>
      </button>
      )}
    </span>
  )
}

/** La pastille d'une section : les fils écrits depuis ses tuiles (ou sur la section elle-même). */
export function PastilleSection({ section, titre }: { section: string; titre: string }) {
  const fils = useFilsTerObs()
  if (!fils) return null
  const n = fils.filsDeSection(section).length
  if (!n) return null
  return (
    <button type="button" className="obs2-fils-pastille obs2-fils-pastille-section"
      aria-label={`${commentaires(n)} sur la section « ${titre} »`}
      title="Voir les commentaires sur cette section"
      onClick={e => fils.ouvrir({ section }, e.currentTarget)}>
      <MessageSquare size={12} aria-hidden="true" />
      <span aria-hidden="true">{n}</span>
    </button>
  )
}
